import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Outlet, useParams, Link } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from './lib/api.js'
import { Shell } from './components/Shell.jsx'
import { Sidebar } from './components/Sidebar.jsx'
import { Spinner } from './components/ui.jsx'
import { VersionTag } from './components/VersionTag.jsx'
import { AvisoVersion } from './components/AvisoVersion.jsx'
import { AyudaProvider, PanelAyuda } from './lib/ayuda.jsx'
import { Paleta } from './components/Paleta.jsx'
import { Login } from './pages/Login.jsx'
import { ProjectsIndex } from './pages/ProjectsIndex.jsx'
import { Overview } from './pages/project/Overview.jsx'
import { Deployments } from './pages/project/Deployments.jsx'
import { BuildLog } from './pages/project/BuildLog.jsx'
import { Tables } from './pages/project/Tables.jsx'
import { Sessions } from './pages/admin/Sessions.jsx'
import { Audit } from './pages/admin/Audit.jsx'
import { Health } from './pages/admin/Health.jsx'
import { NewProject } from './pages/NewProject.jsx'
import { Settings } from './pages/project/Settings.jsx'
import { Functions } from './pages/project/Functions.jsx'
import { Runs } from './pages/project/Runs.jsx'
import { Env } from './pages/project/Env.jsx'
import { Metricas } from './pages/project/Metricas.jsx'
import { Miembros } from './pages/project/Miembros.jsx'
import { Logs } from './pages/project/Logs.jsx'
import { Storage } from './pages/project/Storage.jsx'

// CodeMirror solo se descarga al abrir el editor: quien únicamente mira despliegues no paga
// sus 119 kB. Es el mismo argumento que dejaría a Monaco fuera del arranque si llega en v3.
const Sql = lazy(() => import('./pages/project/Sql.jsx').then((m) => ({ default: m.Sql })))
const FunctionEditor = lazy(() =>
  import('./pages/project/FunctionEditor.jsx').then((m) => ({ default: m.FunctionEditor })))

/**
 * Puerta de entrada a las páginas de un proyecto.
 *
 * Resuelve el proyecto una sola vez y corta aquí si no se puede usar —está eliminándose, o no
 * existe, o no tienes acceso—. Sin esto, cada página soltaba su propio error críptico y, en el
 * caso de una baja en curso, se veían datos de algo que ya no está. La consulta se comparte con
 * las páginas de dentro, así que no cuesta una petición de más.
 */
function ProjectLayout() {
  const { slug } = useParams()
  const q = useQuery({
    queryKey: ['project', slug],
    queryFn: () => api.project(slug),
    retry: (n, err) => !(err instanceof ApiError && [404, 409].includes(err.status)) && n < 2,
  })

  const cuerpo = () => {
    if (q.isPending) return <Spinner />
    if (q.isError) {
      const e = q.error instanceof ApiError ? q.error : null
      return (
        <div className="empty">
          <h3>{e?.status === 409 ? 'Este proyecto no está disponible' : 'Proyecto no encontrado'}</h3>
          <p>
            {e?.body?.detail
              ?? 'No existe, o no tienes acceso a él en GitLab.'}
          </p>
          <Link className="btn" to="/">Volver a proyectos</Link>
        </div>
      )
    }
    return <Outlet />
  }

  return (
    <div className="layout">
      <Sidebar />
      <main className="main">{cuerpo()}</main>
    </div>
  )
}

export function App() {
  const qc = useQueryClient()
  const me = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: (n, err) => !(err instanceof ApiError && err.status === 401) && n < 2,
  })

  if (me.isPending) return <Spinner />
  if (me.isError) {
    // El motivo puede venir del 401 de /api/me o de un redirect del callback (?error=…), que es
    // como llega el rechazo de la lista de acceso: ahí todavía no hay sesión que consultar.
    const deRedirect = new URLSearchParams(window.location.search).get('error')
    const deApi = me.error instanceof ApiError ? me.error.body?.error : null
    return <><Login reason={deRedirect ?? deApi} /><VersionTag /></>
  }

  const logout = async () => {
    await api.logout().catch(() => {})
    qc.clear()
    window.location.href = '/'
  }

  return (
    <AyudaProvider>
    <div className="app-fila">
    <div className="app-col">
    <Shell me={me.data} onLogout={logout}>
      <Routes>
        <Route path="/" element={<ProjectsIndex />} />
        <Route path="/p/:slug" element={<ProjectLayout />}>
          <Route index element={<Overview />} />
          <Route path="deployments" element={<Deployments />} />
          <Route path="b/:deploymentId" element={<BuildLog />} />
          <Route path="tables" element={<Tables />} />
          <Route path="settings" element={<Settings me={me.data} />} />
          <Route path="sql" element={<Suspense fallback={<Spinner />}><Sql /></Suspense>} />
          <Route path="functions" element={<Functions />} />
          <Route path="functions/edit" element={<Suspense fallback={<Spinner />}><FunctionEditor /></Suspense>} />
          <Route path="runs" element={<Runs />} />
          <Route path="runs/:id" element={<Runs />} />
          <Route path="env" element={<Env />} />
          <Route path="metrics" element={<Metricas />} />
          <Route path="members" element={<Miembros />} />
          <Route path="logs" element={<Logs />} />
          <Route path="storage" element={<Storage />} />
        </Route>
        <Route path="/new" element={<NewProject />} />
        <Route path="/admin/sessions" element={<Sessions />} />
        <Route path="/admin/audit" element={<Audit />} />
        <Route path="/admin/health" element={<Health />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <VersionTag />
      <AvisoVersion />
      <Paleta platformAdmin={me.data?.platformAdmin === true} />
    </Shell>
    </div>
    <PanelAyuda />
    </div>
    </AyudaProvider>
  )
}
